import { createClient } from '@supabase/supabase-js';

// Supabase configuration
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables. Please check your .env.local file.\n' +
    'Required variables:\n' +
    '- REACT_APP_SUPABASE_URL\n' +
    '- REACT_APP_SUPABASE_ANON_KEY\n\n' +
    'See SUPABASE_SETUP_GUIDE.md for setup instructions.'
  );
}

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Database types for TypeScript
export interface DatabaseOrder {
  id: string;
  order_number: string;
  customer_info: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    postalCode: string;
    country: string;
    company?: string;
    vatNumber?: string;
  };
  items: Array<{
    id: number;
    title: string;
    size: string;
    price: number;
    quantity: number;
  }>;
  totals: {
    subtotal: number;
    iva: number;
    shipping: number;
    finalTotal: number;
    shippingZone: string;
    isFreeShipping: boolean;
  };
  payment_details: {
    id: string;
    status: string;
    [key: string]: any;
  };
  email_status: {
    businessEmailSent: boolean;
    customerEmailSent: boolean;
  };
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  created_at: string;
  updated_at: string;
}

// Order status options
export const ORDER_STATUSES = {
  PENDING: 'pending',
  PROCESSING: 'processing', 
  SHIPPED: 'shipped',
  DELIVERED: 'delivered',
  CANCELLED: 'cancelled'
} as const;

export type OrderStatus = typeof ORDER_STATUSES[keyof typeof ORDER_STATUSES];

// Gallery system types
export interface DatabaseGallery {
  id: string;
  token: string;
  title: string;
  client_name: string;
  client_email?: string;
  price_per_image: number;
  is_active: boolean;
  expires_at?: string;
  created_at: string;
  updated_at: string;
}

export interface DatabaseGalleryImage {
  id: string;
  gallery_id: string;
  filename: string;
  original_url?: string;
  preview_url: string;
  price: number;
  position: number;
  created_at: string;
}

export interface DatabaseImagePurchase {
  id: string;
  gallery_id: string;
  image_id: string;
  client_email: string;
  payment_intent_id?: string;
  amount: number;
  status: string;
  download_token?: string;
  purchased_at: string;
}

// Client-facing gallery types
export interface ClientGallery {
  token: string;
  title: string;
  clientName: string;
  pricePerImage: number;
  images: ClientGalleryImage[];
  expiresAt?: string;
  isExpired?: boolean;
  daysUntilExpiry?: number;
  hasAccess?: boolean; // Whether client has purchased access to this gallery
}

export interface ClientGalleryImage {
  id: string;
  filename: string;
  previewUrl: string;
  originalUrl?: string; // Only available if client has access
  price: number;
}